import { Component, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { CustomValidators } from '../../_shared/custom-validators';

import { ClientType } from '../../_models/ClientType';
import { Role } from '../../_models/Role';

import { UserService } from '../../_services/user.service';
import { EngineService } from '../../_services/engine.service';
import { Organization } from '../../_models/Organization';
import { Engine } from '../../_models/Engine';
import { ServiceType } from '../../_models/ServiceType';
import { OrganizationService } from '../../_services/organization.service';
import { AuthService } from '../../_services/auth.service';
import { DialogService } from '../../_services/dialog.service';
import { NgxSpinnerService } from 'ngx-spinner';

import { getUrl } from '../../_shared/utils';
import { CueAppService } from '../../_services/cueApp.service';
import { ImageProcessingStage } from '../../_services/photo.service';
import { CueConfig, DEFAULT_APP_CONFIG } from '../../_models/CueConfig';
import { JsonVisualEditorComponent } from '../../json-visual-editor/json-visual-editor.component';
import { Environment } from '../../../environments/environment';

@Component({
    selector: 'app-client-wizard',
    templateUrl: './client-wizard.component.html',
    styleUrls: ['./client-wizard.component.css']
})

export class ClientWizardComponent implements OnInit {
    get hasAllUsers() {
        return this._hasAllUsers;
    }
    get hasOrgUsers() {
        return this._hasOrgUsers;
    }
    get staffUser() {
        return this._staffUser;
    }

    public formControls(name: string): FormGroup  {
        return this.clientForm?.controls[name] as FormGroup;
    }

    constructor(
        private authService: AuthService,
        private userService: UserService,
        private organizationService: OrganizationService,
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private spinner: NgxSpinnerService,
        private dialogService: DialogService,
        private viewRef: ViewContainerRef,
        private engineService: EngineService,
        private appService: CueAppService
    ) { }

    // Environment Variables
    baseUrl = getUrl();

    public environmentName: string;

    public clientTypes: ClientType[] = [];
    public organizations: Organization[] = [];
    public engines: Engine[] = [];
    public roles: Role[] = [];
    public serviceTypes: ServiceType[] = [];
    public configs: CueConfig[] = [];
    public hasValidationErrors: boolean;

    public selectedServiceTypeIds: number[] = [];

    private _hasAllUsers: boolean;
    private _hasOrgUsers: boolean;
    private _staffUser: boolean;

    public loading = false;

    public clientForm: FormGroup;

    public userSubmitted = false;

    public finalLaunchScreenBackgroundUrl: string;
    public finalLaunchScreenForegroundUrl: string;
    public finalIosAppIconUrl: string;
    public finalAndroidAppIconUrl: string;

    @ViewChild('jsonEditor', { static: false })
    private jsonEditor: JsonVisualEditorComponent;

    public appConfigObj = DEFAULT_APP_CONFIG;

    public outputJson: any;

    public appConfigFileName = 'appData.json';

    public latestAndroidVersion = '0.0.0';

    public latestIosVersion = '0.0.0';

    ngOnInit() {
        this.spinner.show();
        this.loading = true;

        this.userService.hasSinglePermission('ViewAllUsers').pipe(map(
            data => { this._hasAllUsers = data; })).subscribe();

        this.userService.hasSinglePermission('ViewOrganizationUsers').pipe(map(
            data => { this._hasOrgUsers = data; })).subscribe();

        this.userService.hasSinglePermission('ReportActivity').pipe(map(
            data => { this._staffUser = data; })).subscribe();

        // Set up validation
        this.clientForm = this.fb.group({
            userShouldNotify: [true],
            userEnableSubscriptionPeriod: [false],
            userEmail: ['', [Validators.email, Validators.required]],
            userPhoneNumber: [''],
            userClientTypeId: [0, Validators.required],
            userOrganizationId: [0, Validators.required],
            userRoleId: [0, Validators.required],
            userSubscriptionPeriod: { value: { startDate: new Date(), endDate: new Date(), isNew: false }, disabled: true },
            engineApplicationName: ['', [Validators.required, Validators.pattern('^\\S+$')]],
            engineApiKey: ['', Validators.required],
            engineConfigId: [0, [Validators.required, Validators.min(1)]],
            engineNotes: [''],
            imageSegmentStartTimeThreshold: [0, [Validators.required, Validators.min(0), Validators.max(60)]],
            engineGenerateDemoServices: [true],
            canProgramCustomTracks: [false],
            enableNetworkSync: [false],
            canEditVenues: [false],
            enableGitHubActions: [true],
            app: this.fb.group({
                displayName: ['', CustomValidators.conditionalValidator(_ => this.isCustomAppType() || this.isYinzcamAppType(), [Validators.required])],
                iosDeveloperAccountName: ['', CustomValidators.conditionalValidator(this.isCustomAppType, [Validators.required])],
                appType: [-1, Validators.min(0)],
                headerImageUrl: ['', Validators.required],
                launchScreenForegroundUrl: ['', CustomValidators.conditionalValidator(this.isCustomAppType, [Validators.required])],
                launchScreenBackgroundUrl: ['', CustomValidators.conditionalValidator(this.isCustomAppType, [Validators.required])],
                iosAppIconUrl: ['', CustomValidators.conditionalValidator(this.isCustomAppType, [Validators.required])],
                androidAppIconUrl: ['', CustomValidators.conditionalValidator(this.isCustomAppType, [Validators.required])],
                androidVersion: ['', CustomValidators.conditionalValidator(this.isYinzcamAppType, [Validators.required])],
                iosVersion: ['', CustomValidators.conditionalValidator(this.isYinzcamAppType, [Validators.required])],
            })
        });

        const organizationObs = this.organizationService.getAllOrganizations();
        const rolesObs = this.userService.getRoles();
        const engineObs = this.engineService.getEngineServiceTypes();
        const configObs = this.engineService.getConfigs();
        const androidVersionObs = this.appService.getLatestAndroidVersion();
        const iosVersionObs = this.appService.getLatestIosVersion();

        forkJoin([organizationObs, rolesObs, engineObs, configObs, androidVersionObs, iosVersionObs]).subscribe(results => {
            this.organizations = results[0];
            this.roles = results[1];
            this.serviceTypes = results[2];
            this.configs = results[3];
            this.configs = this.configs.filter(c => c.name !== 'appData.json' && c.name !== '.DS_Store');

            this.latestAndroidVersion = results[4].version;
            this.latestIosVersion = results[5].version;

            if (this.organizations != null) {
                const organizationId = this.organizations[0].organizationId.valueOf();
                this.userService.getClientTypes(organizationId).subscribe(data => {
                    this.clientTypes = data;

                    this.clientForm.get('userOrganizationId').patchValue(organizationId);

                    if (this.roles != null) {
                        const roleId = this.roles[0].roleId;
                        this.clientForm.get('userRoleId').patchValue(roleId);
                    }
                    const clientTypeId = this.clientTypes[0].clientTypeId;
                    this.clientForm.get('userClientTypeId').patchValue(clientTypeId);
                });
            }

            this.serviceTypes.forEach(type => {
                this.selectedServiceTypeIds.push(type.serviceTypeId);
            });

            this.loading = false;
            this.spinner.hide();
        });

        this.environmentName = Environment.name;
    }

    compare(val1, val2) {
        return val1 === val2;
      }

    canDeactivate(): Promise<boolean> | boolean {
        if (!this.clientForm.dirty || this.userSubmitted) { return true; }
        // Return a Promise<boolean>
        return this.dialogService.confirm(
            'Unsaved Changes',
            'Are you sure you want to continue? Any unsaved changes will be lost!',
            this.viewRef
        );
    }

    public onSelectOrganization() {
        this.loading = true;
        const organizationId: number = this.clientForm.get('userOrganizationId').value;
        this.userService.getClientTypes(organizationId).subscribe(data => {
            this.clientTypes = data;
            if (this.clientTypes.length > 0) {
                this.clientForm.get('userClientTypeId').patchValue(this.clientTypes[0].clientTypeId);
            }
            this.loading = false;
        });
    }

    public submitNewClient() {

        this.hasValidationErrors = !this.clientForm.valid;
        const invalid = [];
        const controls = this.clientForm.controls;
        for (const name in controls) {
            if (controls[name].invalid) {
                invalid.push(name);
            }
        }
        if (this.hasValidationErrors) {
            this.clientForm.markAllAsTouched();
            this.clientForm.markAsDirty();
            return;
        }

        this.loading = true;

        this.userSubmitted = true;

        const newPassword = [];
        const possibleChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*_+=';
        const possibleNumbers = '0123456789';
        const possibleSymbols = '!@#$%^&*_+=';

        // add letters
        for (let i = 0; i < 8; i++) {
            newPassword.push(possibleChars.charAt(Math.floor(Math.random() * possibleChars.length)));
        }

        // add a number
        newPassword.push(possibleNumbers.charAt(Math.floor(Math.random() * possibleNumbers.length)));

        // add a symbol
        newPassword.push(possibleSymbols.charAt(Math.floor(Math.random() * possibleSymbols.length)));

        let currentIndex = newPassword.length, temporaryValue, randomIndex;

        // shuffle the password
        while (0 !== currentIndex) {
            randomIndex = Math.floor(Math.random() * currentIndex);
            currentIndex -= 1;

            temporaryValue = newPassword[currentIndex];
            newPassword[currentIndex] = newPassword[randomIndex];
            newPassword[randomIndex] = temporaryValue;
        }

        const config = this.configs.find(c => c.cueConfigId === this.clientForm.controls.engineConfigId.value);
        const configObjectPath = `${config.bucketName}:${config.path}/${config.name}`;

        const formData = Object.assign({}, this.clientForm.value);
        const newClient: any = {
            Email: this.clientForm.get('userEmail').value,
            PhoneNumber: this.clientForm.get('userPhoneNumber').value,
            SubscriptionStartDate: this.clientForm.get('userEnableSubscriptionPeriod').value === true ? this.formatDate(formData.userSubscriptionPeriod.startDate) : null,
            SubscriptionEndDate: this.clientForm.get('userEnableSubscriptionPeriod').value === true  ? this.formatDate(formData.userSubscriptionPeriod.endDate) : null,
            OrganizationId: this.clientForm.get('userOrganizationId').value,
            ClientTypeId: this.clientForm.get('userClientTypeId').value,
            RoleId: this.clientForm.get('userRoleId').value,
            Notify: this.clientForm.get('userShouldNotify').value,
            Password: newPassword.join(''),
            applicationName: this.clientForm.get('engineApplicationName').value,
            apiKey: this.clientForm.get('engineApiKey').value,
            config: configObjectPath,
            notes: this.clientForm.get('engineNotes').value,
            imageSegmentStartTimeThreshold: this.clientForm.get('imageSegmentStartTimeThreshold').value,
            createDemos: this.clientForm.get('engineGenerateDemoServices').value,
            serviceTypeIds: this.selectedServiceTypeIds,
            canProgramCustomTracks: this.clientForm.get('canProgramCustomTracks').value,
            enableNetworkSync: this.clientForm.get('enableNetworkSync').value,
            canEditVenues: this.clientForm.get('canEditVenues').value,
            engineConfigId: this.clientForm.get('engineConfigId').value,
            enableGitHubActions: this.clientForm.get('enableGitHubActions').value
        };

        this.createClient(newClient);
    }

    private formatDate(date: Date): string {
        return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    }

    toggleSubscription() {
        if (this.clientForm.get('userEnableSubscriptionPeriod').value === true) {
            this.clientForm.controls.userSubscriptionPeriod.enable();
        } else {
            this.clientForm.controls.userSubscriptionPeriod.disable();
        }
    }

    toggleServiceTypeSelect(serviceTypeId: number) {
        const index = this.selectedServiceTypeIds.indexOf(serviceTypeId);
        if (index > -1) {
            this.selectedServiceTypeIds.splice(index, 1);
        } else {
            this.selectedServiceTypeIds.push(serviceTypeId);
        }
    }

    generateApiKey() {
        const newApiKey = [];
        const possibleChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        for (let i = 0; i < 32; i++) {
            newApiKey.push(possibleChars.charAt(Math.floor(Math.random() * possibleChars.length)));
        }
        this.clientForm.controls.engineApiKey.patchValue(newApiKey.join(''));
    }

    private createClient(newClient: any) {

        this.spinner.show();

        const appGroup = this.formControls('app') as FormGroup;
        this.appService.commitUploads()
            .subscribe(
                _ => {

                    newClient.app = {
                        cueAppId: 0,
                        displayName: (this.formControls('app') as FormGroup).controls.displayName.value,
                        iosDeveloperAccountName: (this.formControls('app') as FormGroup).controls.iosDeveloperAccountName.value,
                        applicationName: this.formControls('engineApplicationName').value,
                        appType: (this.formControls('app') as FormGroup).controls.appType.value,
                        headerImageUrl: (this.formControls('app') as FormGroup).controls.headerImageUrl.value,
                        launchScreenForegroundUrl: this.finalLaunchScreenForegroundUrl,
                        launchScreenBackgroundUrl: this.finalLaunchScreenBackgroundUrl,
                        iosAppIconUrl: this.finalIosAppIconUrl,
                        androidAppIconUrl: this.finalAndroidAppIconUrl,
                        androidVersion: appGroup.controls.androidVersion.value,
                        iosVersion: appGroup.controls.iosVersion.value
                    };

                    this.outputJson.clientImageUrl = (this.formControls('app') as FormGroup).controls.headerImageUrl.value;

                    newClient.AppConfig = {
                        name: "appData.json",
                        path: `LiveConfigs/${this.formControls('engineApplicationName').value}`,
                        bucketName: "cueaudio.com-shared-configs",
                        value: this.outputJson
                    };

                    this.userService.createClient(newClient)
                        .subscribe(
                            _ => {

                                this.dialogService.alert('Success', 'New client registered successfully!', this.viewRef)
                                .then(
                                    () => {
                                        this.spinner.hide();
                                        this.router.navigate(['/portal/users']);
                                    }
                                );
                            }
                        );

                }, error => {
                    console.log(error);
                    if (error.error?.Email) {
                        this.dialogService.alert(
                            'Failed to Create New Client',
                            'A user with that email address already exists. Please choose a different address and try again.',
                            this.viewRef);
                    }
                    this.spinner.hide();
                    this.loading = false;

                }, () => {
                    this.spinner.hide();
                    this.loading = false;
                }
            );
    }

    public onAppTypeChange () {

        const appFG = this.formControls('app') as FormGroup;
        appFG.controls.displayName.updateValueAndValidity();
        appFG.controls.launchScreenForegroundUrl.updateValueAndValidity();
        appFG.controls.launchScreenBackgroundUrl.updateValueAndValidity();
        appFG.controls.iosAppIconUrl.updateValueAndValidity();
        appFG.controls.androidAppIconUrl.updateValueAndValidity();
        appFG.controls.iosDeveloperAccountName.updateValueAndValidity();
    }

    public uploadHeaderImage (file: File) {

        if (file.type !== 'image/png') {

            (this.formControls('app') as FormGroup).controls.headerImageUrl.setErrors({ incorrectFile: true });
            (this.formControls('app') as FormGroup).controls.headerImageUrl.markAsTouched();
            return;
        }

        this.appService.enqueueUploadPhoto(file, `${this.formControls('engineApplicationName').value}/headerImage.png`, { width: 0, height: 0 }, true, false)
            .subscribe(
                res => {

                    (this.formControls('app') as FormGroup).controls.headerImageUrl.patchValue(res.url);
                }
            );
    }

    public uploadLaunchScreenBackgroundImage (file: File) {

        if (file.type !== 'image/jpeg') {

            (this.formControls('app') as FormGroup).controls.launchScreenBackgroundUrl.setErrors({ incorrectFile: true });
            (this.formControls('app') as FormGroup).controls.launchScreenBackgroundUrl.markAsTouched();
            return;
        }

        this.appService.exactImageConstraints(file, 1080, 1920)
            .subscribe(
                res => {

                    if (res) {

                    this.appService.enqueueUploadPhoto(file, `resources/${this.formControls('engineApplicationName').value}/launch.jpg`, { width: 1080, height: 1920 }, true, false)
                        .subscribe(
                            res => {

                                if (res.stage === ImageProcessingStage.Uploaded) {

                                    this.finalLaunchScreenBackgroundUrl = res.url;

                                } else {

                                    (this.formControls('app') as FormGroup).controls.launchScreenBackgroundUrl.patchValue(res.url);
                                }
                            }
                        );

                    } else {

                        (this.formControls('app') as FormGroup).controls.launchScreenBackgroundUrl.setErrors({ incorrectFile: true });
                        (this.formControls('app') as FormGroup).controls.launchScreenBackgroundUrl.markAsTouched();
                    }
                }
            );
    }

    public uploadLaunchScreenForegroundImage (file: File) {

        if (file.type !== 'image/png') {

            (this.formControls('app') as FormGroup).controls.launchScreenForegroundUrl.setErrors({ incorrectFile: true });
            (this.formControls('app') as FormGroup).controls.launchScreenForegroundUrl.markAsTouched();
            return;
        }

        this.appService.exactImageConstraints(file, 1080, 1920)
            .subscribe(
                res => {

                    if (res) {

                        this.appService.enqueueUploadPhoto(file, `resources/${this.formControls('engineApplicationName').value}/launchoverlay.png`, { width: 1080, height: 1920 }, true, false)
                            .subscribe(
                                res => {

                                    if (res.stage === ImageProcessingStage.Uploaded) {

                                        this.finalLaunchScreenForegroundUrl = res.url;

                                    } else {

                                        (this.formControls('app') as FormGroup).controls.launchScreenForegroundUrl.patchValue(res.url);
                                    }
                                }
                            );

                    } else {

                        (this.formControls('app') as FormGroup).controls.launchScreenForegroundUrl.setErrors({ incorrectFile: true });
                        (this.formControls('app') as FormGroup).controls.launchScreenForegroundUrl.markAsTouched();
                    }
                }
            );
    }

    public uploadAndroidAppIconImage (file: File) {

        if (file.type !== 'image/png') {

            (this.formControls('app') as FormGroup).controls.androidAppIconUrl.setErrors({ incorrectFile: true });
            (this.formControls('app') as FormGroup).controls.androidAppIconUrl.markAsTouched();
            return;
        }

        this.spinner.show();

        this.appService.exactImageConstraints(file, 512, 512)
            .subscribe(
                res => {

                    if (res) {

                        this.appService.enqueueUploadPhoto(file, `resources/${this.formControls('engineApplicationName').value}/app_icons/playstore.png`, { width: 512, height: 512 }, true, false)
                            .subscribe(
                                res => {

                                    if (res.stage === ImageProcessingStage.Uploaded) {

                                        this.finalAndroidAppIconUrl = res.url;

                                    } else {

                                        (this.formControls('app') as FormGroup).controls.androidAppIconUrl.patchValue(res.url);
                                    }
                                }
                            );

                        this.appService.uploadAndroidAppIcons(file, this.formControls('engineApplicationName').value)
                                .subscribe(
                                    _ => {

                                        this.spinner.hide();

                                    }, error => {

                                        this.spinner.hide();
                                    }
                                );

                    } else {

                        (this.formControls('app') as FormGroup).controls.androidAppIconUrl.setErrors({ incorrectFile: true });
                        (this.formControls('app') as FormGroup).controls.androidAppIconUrl.markAsTouched();
                    }
                }
            );
    }

    public uploadIosAppIconImage (file: File) {

        if (file.type !== 'image/png') {

            (this.formControls('app') as FormGroup).controls.iosAppIconUrl.setErrors({ incorrectFile: true });
            (this.formControls('app') as FormGroup).controls.iosAppIconUrl.markAsTouched();
            return;
        }

        this.spinner.show();

        this.appService.exactImageConstraints(file, 1024, 1024)
            .subscribe(
                res => {

                    if (res) {

                        this.appService.enqueueUploadPhoto(file, `resources/${this.formControls('engineApplicationName').value}/app_icons/appstore.png`, { width: 1024, height: 1024 }, true, false)
                            .subscribe(
                                res => {

                                    if (res.stage === ImageProcessingStage.Uploaded) {

                                        this.finalIosAppIconUrl = res.url;

                                    } else {

                                        (this.formControls('app') as FormGroup).controls.iosAppIconUrl.patchValue(res.url);
                                    }
                                }
                            );

                        this.appService.uploadIosAppIcons(file, this.formControls('engineApplicationName').value)
                                .subscribe(
                                    _ => {

                                        this.spinner.hide();

                                    }, error => {

                                        this.spinner.hide();
                                    }
                                );

                    } else {

                        (this.formControls('app') as FormGroup).controls.iosAppIconUrl.setErrors({ incorrectFile: true });
                        (this.formControls('app') as FormGroup).controls.iosAppIconUrl.markAsTouched();
                    }
                }
            );
    }

    public preventWhiteSpace (control: AbstractControl) {

            const controlValue = control.value as string;

            control.setValue(controlValue.trim());
            control.updateValueAndValidity();
    }

    public applicationNameChange() {

        if (this.jsonEditor) {

            const clientImageUrlField = this.jsonEditor.jsonEditorFields?.find(f => f.key === "clientImageUrl");

        if (clientImageUrlField) {

            clientImageUrlField.value = "https://cuebranding.cueaudio.com/" + this.clientForm?.controls.engineApplicationName.value + "/headerImage.png";
            clientImageUrlField.disableValue = true;

            // Deep copy to trigger change detection
            this.jsonEditor.jsonEditorFields = JSON.parse(JSON.stringify(this.jsonEditor.jsonEditorFields));
            this.jsonEditor.reload();
        }
        }
    }

    private isYinzcamAppType = () => {

        return this?.formControls('app')?.controls?.appType.value === 2;
    }

    private isCustomAppType = () => {

        return this?.formControls('app')?.controls?.appType.value === 1;
    }

    private isSdkAppType = () => {

        return this?.formControls('app')?.controls?.appType.value === 0;
    }
}
